import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import './BusinessInfoEditor.css';
import ImageInput from './ImageInput';

const DAY_LABEL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function blankForm() {
  return {
    bioTitle: '', bioBody: '', bioPhoto: '', credentials: '', philosophy: '',
    addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '',
    phone: '', email: '', mapEmbedUrl: '',
    licenseNumber: '', establishmentNumber: '',
    hours: [1,2,3,4,5,6,0].map((d) => ({ dayOfWeek: d, isOpen: false, openTime: '', closeTime: '', note: '' })),
  };
}

function mergeHours(existing = []) {
  const base = blankForm().hours;
  return base.map((row) => {
    const found = existing.find((h) => h.dayOfWeek === row.dayOfWeek);
    return found ? {
      dayOfWeek: row.dayOfWeek,
      isOpen: !!found.isOpen,
      openTime: found.openTime || '',
      closeTime: found.closeTime || '',
      note: found.note || '',
    } : row;
  });
}

export default function BusinessInfoEditor() {
  const { authenticatedFetch } = useAuth();
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API_BASE_URL}/api/business-info`);
      if (!res.ok) return;
      const data = await res.json();
      setForm({
        bioTitle: data.bioTitle || '', bioBody: data.bioBody || '', bioPhoto: data.bioPhoto || '',
        credentials: data.credentials || '', philosophy: data.philosophy || '',
        addressLine1: data.addressLine1 || '', addressLine2: data.addressLine2 || '',
        city: data.city || '', state: data.state || '', postalCode: data.postalCode || '',
        phone: data.phone || '', email: data.email || '', mapEmbedUrl: data.mapEmbedUrl || '',
        licenseNumber: data.licenseNumber || '', establishmentNumber: data.establishmentNumber || '',
        hours: mergeHours(data.hours),
      });
    })();
  }, []);

  function update(field, value) { setForm((f) => ({ ...f, [field]: value })); }
  function updateHour(idx, patch) { setForm((f) => ({ ...f, hours: f.hours.map((h, i) => i === idx ? { ...h, ...patch } : h) })); }

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, hours: form.hours.map((h) => ({
      dayOfWeek: h.dayOfWeek, isOpen: h.isOpen,
      openTime: h.openTime || null, closeTime: h.closeTime || null, note: h.note || null,
    })) };
    const res = await authenticatedFetch(`${API_BASE_URL}/api/business-info`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    setSaving(false);
    if (!res.ok) { alert('Save failed'); return; }
    alert('Saved');
  }

  return (
    <form className="business-info-editor" onSubmit={onSave}>
      <h2>Business Info</h2>

      <fieldset>
        <legend>About</legend>
        <label>Bio title <input value={form.bioTitle} onChange={(e) => update('bioTitle', e.target.value)} /></label>
        <label>Bio body <textarea rows={6} value={form.bioBody} onChange={(e) => update('bioBody', e.target.value)} /></label>
        <ImageInput label="Bio photo" value={form.bioPhoto} onChange={(v) => update('bioPhoto', v)} />
        <label>Credentials <textarea rows={4} value={form.credentials} onChange={(e) => update('credentials', e.target.value)} /></label>
        <label>Philosophy <textarea rows={4} value={form.philosophy} onChange={(e) => update('philosophy', e.target.value)} /></label>
      </fieldset>

      <fieldset>
        <legend>Licensing</legend>
        <label>Massage license number <input value={form.licenseNumber} onChange={(e) => update('licenseNumber', e.target.value)} /></label>
        <label>Establishment number <input value={form.establishmentNumber} onChange={(e) => update('establishmentNumber', e.target.value)} /></label>
      </fieldset>

      <fieldset>
        <legend>Contact &amp; Location</legend>
        <label>Address line 1 <input value={form.addressLine1} onChange={(e) => update('addressLine1', e.target.value)} /></label>
        <label>Address line 2 <input value={form.addressLine2} onChange={(e) => update('addressLine2', e.target.value)} /></label>
        <label>City <input value={form.city} onChange={(e) => update('city', e.target.value)} /></label>
        <label>State <input value={form.state} onChange={(e) => update('state', e.target.value)} /></label>
        <label>Postal code <input value={form.postalCode} onChange={(e) => update('postalCode', e.target.value)} /></label>
        <label>Phone <input value={form.phone} onChange={(e) => update('phone', e.target.value)} /></label>
        <label>Email <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} /></label>
        <label>Google Maps embed URL <input value={form.mapEmbedUrl} onChange={(e) => update('mapEmbedUrl', e.target.value)} placeholder="Paste the src from Google Maps → Share → Embed" /></label>
      </fieldset>

      <fieldset>
        <legend>Hours</legend>
        <table className="hours-table">
          <thead><tr><th>Day</th><th>Open?</th><th>Open</th><th>Close</th><th>Note</th></tr></thead>
          <tbody>
            {form.hours.map((h, i) => (
              <tr key={h.dayOfWeek}>
                <td>{DAY_LABEL[h.dayOfWeek]}</td>
                <td><input type="checkbox" checked={h.isOpen} onChange={(e) => updateHour(i, { isOpen: e.target.checked })} /></td>
                <td><input type="time" value={h.openTime} disabled={!h.isOpen} onChange={(e) => updateHour(i, { openTime: e.target.value })} /></td>
                <td><input type="time" value={h.closeTime} disabled={!h.isOpen} onChange={(e) => updateHour(i, { closeTime: e.target.value })} /></td>
                <td><input value={h.note} onChange={(e) => updateHour(i, { note: e.target.value })} placeholder="e.g. By appointment" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </fieldset>

      <div className="editor-actions">
        <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}
